import { useState, useEffect } from 'react';
import { createProject, updateProject } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Loader2, Shield, Settings, Eye, Users, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  PROJECT_TYPES,
  PROJECT_STAGES,
  ALLOCATION_MODES,
  PRICE_MASKING_MODES,
  DEFAULT_FORM_DATA,
} from './projectConstants';

export const ProjectFormDialog = ({ isOpen, onOpenChange, editingProject, developerId, onSaved }) => {
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA, developer_id: developerId });
  const [activeTab, setActiveTab] = useState('basic');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editingProject) {
      setFormData({
        name: editingProject.name || '',
        code: editingProject.code || '',
        location: editingProject.location || '',
        description: editingProject.description || '',
        developer_id: editingProject.developer_id,
        image_url: editingProject.image_url || '',
        project_type: editingProject.project_type || 'apartments',
        project_stage: editingProject.project_stage || 'pre_launch',
        rera_registered: editingProject.rera_registered || false,
        rera_number: editingProject.rera_number || '',
        sales_start_date: editingProject.sales_start_date || '',
        expected_completion_date: editingProject.expected_completion_date || '',
        inventory_behavior: editingProject.inventory_behavior || DEFAULT_FORM_DATA.inventory_behavior,
        channel_strategy: editingProject.channel_strategy || DEFAULT_FORM_DATA.channel_strategy,
        pricing_visibility: editingProject.pricing_visibility || DEFAULT_FORM_DATA.pricing_visibility,
        allocation_mode: editingProject.allocation_mode || 'first_come_first_serve',
        linked_crm: editingProject.linked_crm || '',
        crm_project_id: editingProject.crm_project_id || ''
      });
    } else {
      setFormData({ ...DEFAULT_FORM_DATA, developer_id: developerId });
    }
    setActiveTab('basic');
  }, [isOpen, editingProject, developerId]);

  const updateInventoryBehavior = (field, value) => {
    setFormData(prev => ({
      ...prev,
      inventory_behavior: { ...prev.inventory_behavior, [field]: value }
    }));
  };

  const updateChannelStrategy = (field, value) => {
    setFormData(prev => ({
      ...prev,
      channel_strategy: { ...prev.channel_strategy, [field]: value }
    }));
  };

  const updatePricingVisibility = (field, value) => {
    setFormData(prev => ({
      ...prev,
      pricing_visibility: { ...prev.pricing_visibility, [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        rera_number: formData.rera_registered ? formData.rera_number : null,
        sales_start_date: formData.sales_start_date || null,
        expected_completion_date: formData.expected_completion_date || null,
        linked_crm: formData.linked_crm || null,
        crm_project_id: formData.crm_project_id || null
      };

      if (editingProject) {
        await updateProject(editingProject.project_id, submitData);
        toast.success('Project updated successfully');
      } else {
        await createProject(submitData);
        toast.success('Project created successfully');
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      console.error('Failed to save project:', error);
      toast.error(error.message || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{editingProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
          <DialogDescription>
            {editingProject ? 'Update project details and settings' : 'Add a new real estate project'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-4">
              <TabsTrigger value="basic" className="text-xs">Basic</TabsTrigger>
              <TabsTrigger value="rera" className="text-xs">RERA</TabsTrigger>
              <TabsTrigger value="inventory" className="text-xs">Inventory</TabsTrigger>
              <TabsTrigger value="channels" className="text-xs">Channels</TabsTrigger>
              <TabsTrigger value="crm" className="text-xs">CRM</TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[400px] pr-4">
              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Project Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Sunset Heights"
                      required
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="code">Project Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="SH001"
                      required
                      data-testid="project-code-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project Type *</Label>
                    <Select 
                      value={formData.project_type} 
                      onValueChange={(v) => setFormData({ ...formData, project_type: v })}
                    >
                      <SelectTrigger data-testid="project-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Project Stage *</Label>
                    <Select 
                      value={formData.project_stage} 
                      onValueChange={(v) => setFormData({ ...formData, project_stage: v })}
                    >
                      <SelectTrigger data-testid="project-stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_STAGES.map(stage => (
                          <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Jaipur, Rajasthan"
                    data-testid="project-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Premium residential project..."
                    rows={3}
                    data-testid="project-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sales Start Date</Label>
                    <Input
                      type="date"
                      value={formData.sales_start_date}
                      onChange={(e) => setFormData({ ...formData, sales_start_date: e.target.value })}
                      data-testid="sales-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expected Completion Date</Label>
                    <Input
                      type="date"
                      value={formData.expected_completion_date}
                      onChange={(e) => setFormData({ ...formData, expected_completion_date: e.target.value })}
                      data-testid="completion-date"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Allocation Mode</Label>
                  <Select 
                    value={formData.allocation_mode} 
                    onValueChange={(v) => setFormData({ ...formData, allocation_mode: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALLOCATION_MODES.map(mode => (
                        <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image_url">Image URL (Optional)</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="project-image-input"
                  />
                </div>
              </TabsContent>

              {/* RERA Tab */}
              <TabsContent value="rera" className="space-y-4 mt-0">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">RERA Details</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>RERA Registered?</Label>
                        <p className="text-xs text-muted-foreground">Is this project registered under RERA?</p>
                      </div>
                      <Switch
                        checked={formData.rera_registered}
                        onCheckedChange={(v) => setFormData({ ...formData, rera_registered: v })}
                        data-testid="rera-registered-switch"
                      />
                    </div>
                    
                    {formData.rera_registered && (
                      <div className="space-y-2">
                        <Label htmlFor="rera_number">RERA Number</Label>
                        <Input
                          id="rera_number"
                          value={formData.rera_number}
                          onChange={(e) => setFormData({ ...formData, rera_number: e.target.value })}
                          placeholder="RAJ/REG/2024/..."
                          data-testid="rera-number-input"
                        />
                      </div>
                    )}
                  </div>
                </Card>
              </TabsContent>

              {/* Inventory Behavior Tab */}
              <TabsContent value="inventory" className="space-y-4 mt-0">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Default Inventory Behavior</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Default Soft Block Duration (hours)</Label>
                      <Select 
                        value={formData.inventory_behavior.default_soft_block_hours.toString()} 
                        onValueChange={(v) => updateInventoryBehavior('default_soft_block_hours', parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2">2 hours</SelectItem>
                          <SelectItem value="4">4 hours</SelectItem>
                          <SelectItem value="8">8 hours</SelectItem>
                          <SelectItem value="12">12 hours</SelectItem>
                          <SelectItem value="24">24 hours</SelectItem>
                          <SelectItem value="48">48 hours</SelectItem>
                          <SelectItem value="72">72 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Allow CP Soft Block?</Label>
                        <p className="text-xs text-muted-foreground">Channel partners can soft block units</p>
                      </div>
                      <Switch
                        checked={formData.inventory_behavior.allow_cp_soft_block}
                        onCheckedChange={(v) => updateInventoryBehavior('allow_cp_soft_block', v)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Allow Website Visibility?</Label>
                        <p className="text-xs text-muted-foreground">Show inventory on public website</p>
                      </div>
                      <Switch
                        checked={formData.inventory_behavior.allow_website_visibility}
                        onCheckedChange={(v) => updateInventoryBehavior('allow_website_visibility', v)}
                      />
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Pricing Visibility</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Show Pricing to Sales</Label>
                      </div>
                      <Switch
                        checked={formData.pricing_visibility.show_to_sales}
                        onCheckedChange={(v) => updatePricingVisibility('show_to_sales', v)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Show Pricing to Channel Partners</Label>
                      </div>
                      <Switch
                        checked={formData.pricing_visibility.show_to_cp}
                        onCheckedChange={(v) => updatePricingVisibility('show_to_cp', v)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Show Pricing on Website</Label>
                      </div>
                      <Switch
                        checked={formData.pricing_visibility.show_to_website}
                        onCheckedChange={(v) => updatePricingVisibility('show_to_website', v)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Price Masking Mode</Label>
                      <Select 
                        value={formData.pricing_visibility.masking_mode} 
                        onValueChange={(v) => updatePricingVisibility('masking_mode', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRICE_MASKING_MODES.map(mode => (
                            <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Channels Tab */}
              <TabsContent value="channels" className="space-y-4 mt-0">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Channel Strategy</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Enable Channel Partners</Label>
                        <p className="text-xs text-muted-foreground">Allow external partners to sell units</p>
                      </div>
                      <Switch
                        checked={formData.channel_strategy.enable_channel_partners}
                        onCheckedChange={(v) => updateChannelStrategy('enable_channel_partners', v)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Enable Website Booking</Label>
                        <p className="text-xs text-muted-foreground">Allow bookings from website</p>
                      </div>
                      <Switch
                        checked={formData.channel_strategy.enable_website_booking}
                        onCheckedChange={(v) => updateChannelStrategy('enable_website_booking', v)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Internal Sales Only</Label>
                        <p className="text-xs text-muted-foreground">Restrict to internal team only</p>
                      </div>
                      <Switch
                        checked={formData.channel_strategy.internal_sales_only}
                        onCheckedChange={(v) => updateChannelStrategy('internal_sales_only', v)}
                      />
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* CRM Tab */}
              <TabsContent value="crm" className="space-y-4 mt-0">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">CRM Integration</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Linked CRM</Label>
                      <Select 
                        value={formData.linked_crm || 'none'} 
                        onValueChange={(v) => setFormData({ ...formData, linked_crm: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select CRM" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="salesforce">Salesforce</SelectItem>
                          <SelectItem value="hubspot">HubSpot</SelectItem>
                          <SelectItem value="zoho">Zoho CRM</SelectItem>
                          <SelectItem value="leadsquared">LeadSquared</SelectItem>
                          <SelectItem value="sell_do">Sell.Do</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.linked_crm && (
                      <div className="space-y-2">
                        <Label htmlFor="crm_project_id">CRM Project ID</Label>
                        <Input
                          id="crm_project_id"
                          value={formData.crm_project_id}
                          onChange={(e) => setFormData({ ...formData, crm_project_id: e.target.value })}
                          placeholder="Project ID in CRM system"
                        />
                      </div>
                    )}
                  </div>
                </Card>
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="save-project-btn">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingProject ? 'Update Project' : 'Create Project'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
